import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useModal } from '../context/ModalContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import ProductModal from './modals/ProductModal';
import AboutModal from './modals/AboutModal';
import ContactModal from './modals/ContactModal';
import SectionModal from './modals/SectionModal';
import NewsArticleModal from './modals/NewsArticleModal';

const widthByType = {
  product: 'max-w-5xl',
  'about-value': 'max-w-3xl',
  contact: 'max-w-xl',
  section: 'max-w-5xl',
  'news-article': 'max-w-3xl',
};

const Modal = () => {
  const { isOpen, modalType, modalData, closeModal } = useModal();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  /* Lock body scroll + Esc key */
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeModal]);

  if (!isOpen) return null;

  const maxWidth = widthByType[modalType] || 'max-w-3xl';

  const renderContent = () => {
    switch (modalType) {
      case 'product':        return <ProductModal data={modalData} />;
      case 'about-value':    return <AboutModal data={modalData} />;
      case 'contact':        return <ContactModal data={modalData} />;
      case 'section':        return <SectionModal data={modalData} />;
      case 'news-article':   return <NewsArticleModal data={modalData} />;
      default:               return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="modal-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm animate-fade-in-up"
        style={{ animationDuration: '0.15s' }}
        onClick={closeModal}
      />

      {/* Panel */}
      <div
        className={`
          relative w-full ${maxWidth} max-h-[90vh] rounded-sm shadow-2xl
          animate-fade-in-up overflow-y-auto overscroll-contain
          ${isDark
            ? 'bg-[#111111] border border-[var(--border-color)]'
            : 'bg-white border border-gray-200'
          }
        `}
        dir={isRtl ? 'rtl' : 'ltr'}
        style={{ animationDuration: '0.25s', WebkitOverflowScrolling: 'touch' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-panel"
      >
        {/* Close button — sticky so it stays visible while scrolling */}
        <div
          className="sticky top-0 z-30 pointer-events-none"
          style={{ height: 0 }}
        >
          <button
            onClick={closeModal}
            className={`
              pointer-events-auto absolute top-4 w-9 h-9 flex items-center justify-center rounded-full
              transition-all duration-200 shadow-md
              ${isRtl ? 'left-4' : 'right-4'}
              ${isDark
                ? 'bg-black/60 hover:bg-black/80 text-white/80 hover:text-white backdrop-blur-sm'
                : 'bg-white/90 hover:bg-white text-gray-500 hover:text-gray-800 backdrop-blur-sm'
              }
            `}
            aria-label="Close"
            data-testid="modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default Modal;
